import { useState, useEffect } from 'react';
import { Modal, Input, Select, Button, Form, Row, Col, message } from 'antd';
import { removeLeadingZeros } from './utils';
import {
  getStudyAnalysisLibraryList,
  getStatsLibraryList
} from 'src/DigitalProtocolModule/utils/ajax-proxy';
import 'antd/dist/antd.css';
import './index.scss';

const { Option } = Select;

interface AnalysisSet {
  analysis_set_id: number;
  analysis_set_map_id: number;
  analysis_set_name: string;  
}

interface statiscalList{
  statistical_test_id: number;
  test_name: string;
}

interface EndpointDetailsModalProps {
  visible: boolean;
  onCancel: () => void;
  onSave: (data: any, isNameReadOnly: boolean) => void;  // You might want to define a more specific type for the data
  initialData: any;  // Define a more specific type if possible
  isNameReadOnly: boolean;
  isEditData: boolean;
  unit: string;  // Since it seems unit is either 'Day' or 'Week'
}

// Common Modal Content Component
const EndpointDetailsModal = ({ visible, onCancel, onSave, initialData, isNameReadOnly, isEditData, unit }: EndpointDetailsModalProps) => {
  const trial_uuid = localStorage.getItem("trial-id");
	const [formData, setFormData] = useState(initialData || {});
  const [analysisList, setAnalysisList] = useState<AnalysisSet[]>([]);
  const [statiscalList, setStatiscalList] = useState<statiscalList[]>([]);
  const [searchValue, setSearchValue] = useState('');  
  const [limitExceedMsg, setLimitExceedMsg] = useState('');
  const [timepoints, setTimepoints] = useState<number>(0);
  const [weeks, setWeeks] = useState(unit === 'Week' ? (initialData?.weeks || Array(timepoints).fill('')) : Array(timepoints).fill(''));
  const [days, setDays] = useState(unit === 'Day' ? (initialData?.days || Array(timepoints).fill('')) : Array(timepoints).fill(''));


  useEffect(() => {    
    if(isEditData){
      // Find matching analysis sets based on initialData
      const matchingAnalysisSets = analysisList.filter(set =>
        initialData?.analysis_set_map_id?.includes(set.analysis_set_map_id)
      );
      const mappedData = {
        ...initialData,
        endpointName: initialData?.endpoint_name || '',
        description: initialData?.endpoint_description || '',       
        statisticalMethod: initialData?.test_name || undefined,
        analysisSet: matchingAnalysisSets.map(set => set.analysis_set_map_id), // Map to array of IDs
        parameters: initialData?.parameters || '',
        significanceLevel: initialData?.significance_level || '',
        confidenceInterval: initialData?.confidence_interval || '',
        nSides: initialData?.n_sides || '',        
        days: initialData?.day || [],
        weeks: initialData?.week || [],
      };           
      setFormData(mappedData);
      const dayLength = initialData?.day ? initialData.day.length : 0;
      const weekLength = initialData?.week ? initialData.week.length : 0;
      setTimepoints(unit === 'Day' ? dayLength.toString() : weekLength.toString() || '2');
      setWeeks(initialData?.week || []);
      setDays(initialData?.day || []);
    }else{
      setFormData(initialData);
      setTimepoints(initialData?.timepoints);
    }    
  }, [initialData, unit, isEditData, analysisList]);

  useEffect(() => {
    setFormData(prevFormData => ({
      ...prevFormData,
      weeks,
      days
    }));
  }, [weeks, days]);

  const weekChangeHandler = (e, idx) => {
    const regex = /^-?(?!$)(?!.*--)\d+$/;
    const value = e.target.value;
    const val:any = value === '-' ? '-' : (value === '' ? null : (regex.test(value) ? parseInt(value) : null));   

    if (val !== '-' && ((unit === 'Week' && (val > 1000 || val < -1000)) || (unit === 'Day' && (val > 7000 || val < -7000)))) {  
      return;  
    }  

    const updatedWeeks = [...weeks];  
    const updatedDays = [...days];    

    if (unit === 'Week') {  
      updatedWeeks[idx] = val;  
      updatedDays[idx] = val !== null ? Math.round(val * 7) : null;  
    } else if (unit === 'Day') {  
      updatedDays[idx] = val;  
      updatedWeeks[idx] = val !== null ? Math.round(val / 7) : null;  
    }  
    setWeeks(updatedWeeks);  
    setFormData({ ...formData, weeks: updatedWeeks });
    setDays(updatedDays);
    setFormData({ ...formData, days: updatedDays });
  };

  const handleTimepointsChange = (e) => {
    const newTimepoints = parseInt(e.target.value, 10) || 0;
    if (newTimepoints > 50) {
      setLimitExceedMsg('(Max value allowed - 50)');
      message.error('The number of timepoints cannot exceed 50.');
      return;
    }else{
      setLimitExceedMsg('');
    }
    setTimepoints(newTimepoints);    
    setWeeks(Array(newTimepoints).fill(''));
    setDays(Array(newTimepoints).fill(''));
  };

  const fetchStudynAnalysisLibrarySet = async () => {
    try {          
      const res = await getStudyAnalysisLibraryList(trial_uuid);   
      if(res?.analysis_set_list){
        setAnalysisList(res. analysis_set_list || []);			
      }
    } catch (error) {
      message.error('Failed to fetch the analysissets')
    }
  }

  const fetchStatiscalList = async () => {
    try {    
      const res = await getStatsLibraryList(trial_uuid);     
      if(res?.statistical_test_list) {
        setStatiscalList(res.statistical_test_list || []);	
      }		
    } catch (error) {
      message.error('Failed to fetch the statiscal list')
    }
  }  

  useEffect(()=>{
    fetchStudynAnalysisLibrarySet();
    fetchStatiscalList();
  }, [])



	const handleChange = (e) => {
    const { name, value } = e.target;  

    if (name === 'confidenceInterval') {
      // Use regex to check for negative sign
      if (/^-/.test(value)) {
        message.error('Confidence interval cannot be negative.');
        return;
      }
      
      // // Replace any leading zeros with a single '0'
      let normalizedValue = removeLeadingZeros(value);      

      const numericValue = Number(normalizedValue);
      // Accept values strictly between 0 and 100
      if (numericValue < 0 || numericValue > 100 ) {
        message.error('Confidence interval must be between 0 and 100.');
        return;
      }
      // Update the form data with the normalized value
      setFormData({ ...formData, [name]: normalizedValue });
      return; // Exit the function after setting the data
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (value, name) => {
    setFormData({ ...formData, [name]: value });
    if (name === 'statisticalMethod') {
      setSearchValue(value);
    }
    if (name === 'analysisSet') {
      // Update the analysisSet by spreading the existing values and adding the new ones
      setFormData(prevFormData => ({
          ...prevFormData,
          analysisSet: value
      }));
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleBlurOrMouseLeave = () => {
    // Update the formData with searchValue only if it's not an existing option and searchValue is not empty
    if (
      searchValue &&
      !statiscalList.some(test => test.test_name === searchValue) &&
      searchValue !== formData.statisticalMethod
    ) {
      setFormData(prevFormData => ({ ...prevFormData, statisticalMethod: searchValue }));
    }
  };

  const handleSave = () => {
    const { endpointName, description, significanceLevel } = formData;
    if (!endpointName || !description || !timepoints) {
      message.error('Endpoint name, Description, and Timeframe are required fields.');
      return;
    }    

    // Validate significance level
    const numericSignificanceLevel = significanceLevel ? parseFloat(significanceLevel) : null;
    if (significanceLevel && (isNaN(numericSignificanceLevel) || numericSignificanceLevel <= 0 || numericSignificanceLevel >= 1)) {
      message.error('Significance level must be a decimal between 0 and 1 (exclusive).');
      return;
    }

    // Filter out empty or null values from weeks and days
    const filteredWeeks = weeks.filter(value => value !== '' && value !== null);
    const filteredDays = days.filter(value => value !== '' && value !== null);

    // Check if at least one value is entered in either days or weeks    
    const hasValidWeeks = filteredWeeks.length > 0;
    const hasValidDays = filteredDays.length > 0;

    if (!(hasValidWeeks || hasValidDays)) {
      message.error('Please enter at least one value for either Days or Weeks.');
      return;
    }

    // Update the state with filtered weeks and days
    setWeeks(filteredWeeks);
    setDays(filteredDays);
      
    // Update form data with filtered weeks and days
    const dataToSave = {
      ...formData,
      weeks: filteredWeeks,
      days: filteredDays
    };
  	onSave(dataToSave, isNameReadOnly);
		onCancel(); // Close the modal
  };

  return (
    <Modal
			className='endpointModalContainer'
      title="Add endpoint details"
      visible={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      <div className='endpointContainer'>
        <div>
          <label>Endpoint Name<span className="required">*</span></label>
          <Input
            placeholder='Enter Endpoint Name'
            name="endpointName"
            value={formData?.endpointName || ''}
            onChange={handleChange}
						disabled={isNameReadOnly}
            maxLength={50}
          />
        </div>
        <div>
          <label>Description<span className="required">*</span></label>
          <Input.TextArea
            placeholder="Enter Description"
            name="description"
            value={formData?.description || ''}
            onChange={handleChange}
            maxLength={500}
          />
        </div>
        <div className='timepointsGrid'>
					<div className='endpointGrid'>
						<label>At how many timepoints will the endpoint be measured? <span className="required">*</span></label>
            <div className='timepointsInput'>
              <Input              
                className='weeksInput'							
                name="timepoints"
                value={timepoints || ''}							
                onChange={handleTimepointsChange}              
              />
              <label className='labelStyle'>{limitExceedMsg}</label>
            </div>
					</div>
          {Array.from({ length: timepoints }).map((_, idx) => (
            <div key={idx} className='endpointInput'>              
              <Input
                className='weeksInput'                
                name={`week${idx + 1}`}
                value={
                  unit === 'Week'
                    ? (formData?.weeks ? formData.weeks[idx] : '')
                    : (formData?.days ? formData.days[idx] : '')
                }
                onChange={(e) => weekChangeHandler(e, idx)}
              />
              <label className='labelStyle'>{unit}s</label>
            </div>
          ))}					
        </div>
				<Row gutter={16}>
          <Col span={12}>
						<div className='endpointGrid'>
              <label>Statistical Method</label>
              <Select
                showSearch
                placeholder="Select" 
                value={formData?.statisticalMethod || undefined}
                onSearch={handleSearch}
                onChange={(value) => handleSelectChange(value, 'statisticalMethod')}
                onBlur={handleBlurOrMouseLeave}
                onMouseLeave={handleBlurOrMouseLeave}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                notFoundContent={null}
              >
                {statiscalList?.length > 0 && statiscalList?.map((test) => (
                  <Option key={`${test.statistical_test_id}-${test.test_name}`}  value={test.test_name}>
                    {test.test_name}
                  </Option>
                ))}
              </Select>             
            </div>
          </Col>
          <Col span={12}>
            <div className='endpointGrid'>
              <label>Select Analysis Set</label>
              <Select
                mode="multiple"  // Enable multiple selection
                placeholder="Select"
                value={formData?.analysisSet || []}  // Ensure this is an array
                onChange={(value) => handleSelectChange(value, 'analysisSet')}
              >
                {analysisList?.length > 0 && analysisList?.map((set) => (
                  <Option key={set.analysis_set_map_id} value={set.analysis_set_map_id}>
                    {set.analysis_set_name}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
        </Row>
      
				<Row gutter={16}>
          <Col span={12}>
            <div className='endpointGrid'>
							<label>Parameters</label>
							<Input								
								name="parameters"
								value={formData?.parameters || ''}
								onChange={handleChange}
							/>
        		</div>
          </Col>
          <Col span={12}>
            <div className='endpointGrid'>
              <label>Confidence Interval</label>
              <Input
                type="number"                
                name="confidenceInterval"
                value={formData?.confidenceInterval || ''}
                onChange={handleChange}
              />
            </div>          
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <div className='endpointGrid'>
							<label>Significance Level (Alpha)</label>
							<Input
                type="number"								
								name="significanceLevel"
								value={formData?.significanceLevel || ''}
								onChange={handleChange}
							/>
						</div>
          </Col>          
          <Col span={12}>
            <div className='endpointGrid'>
              <label>N Sides</label>
              <Select
                placeholder="Select"
                value={formData?.nSides || ''}
                onChange={(value) => handleSelectChange(value, 'nSides')}
              >
                <Option value="One Side">One Side</Option>
                <Option value="Two Side">Two Side</Option>
              </Select>
            </div>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default EndpointDetailsModal;